/**
 * Hole Loader — Catalogue of available hole layouts.
 *
 * Holes are stored as JSON data files. This module provides a central
 * registry so the UI can list and load them by ID.
 *
 * Two layout formats are supported:
 *   - Legacy polygon format (zones array) — loaded via holeSimulation.createHoleLayout
 *   - Hex grid format (hexes array + hexSize) — loaded via createHoleLayoutFromHex here
 *
 * The public catalogue API (HOLE_CATALOGUE, getHoleById, listHoles) is unchanged.
 */

import shortPar3 from '../data/holes/short-par3.json';
import islandPar3 from '../data/holes/island-par3.json';
import doglegRightPar4 from '../data/holes/dogleg-right-par4.json';
import sharpDoglegPar4 from '../data/holes/sharp-dogleg-par4.json';
import sweepingPar5 from '../data/holes/sweeping-par5.json';

import {
  buildHexMap,
  hexBounds,
  hexCentreMetres,
  metreToHex,
} from './hexUtils';

/**
 * Full catalogue of available holes.
 * Add new holes here after creating their JSON file.
 */
export const HOLE_CATALOGUE = [
  shortPar3,
  islandPar3,
  doglegRightPar4,
  sharpDoglegPar4,
  sweepingPar5,
];

/**
 * Look up a hole by its `id` field.
 * @param {string} id
 * @returns {Object|undefined}
 */
export function getHoleById(id) {
  return HOLE_CATALOGUE.find(h => h.id === id);
}

/**
 * Return a summary list for UI dropdowns: [{ id, name, par, description }].
 */
export function listHoles() {
  return HOLE_CATALOGUE.map(({ id, name, par, description }) => ({
    id,
    name,
    par,
    description,
  }));
}

// ── Hex format loader ──────────────────────────────────────────────────────

/**
 * Build a metre-per-cell terrain grid from hex data.
 * Each cell in the returned grid is 1m × 1m. The terrain is determined
 * by which hex contains the cell centre (via metreToHex).
 * Unlisted hexes (i.e. OOB) produce 'oob' grid cells.
 *
 * @param {{ hexes: Array, hexSize: number }} hexData
 * @returns {{ grid: string[][], length: number, width: number }}
 */
function buildMetreGrid(hexData) {
  const { hexes, hexSize } = hexData;
  const hexMap = buildHexMap(hexes);
  const bounds = hexBounds(hexes, hexSize);

  const length = Math.ceil(bounds.maxX) + 1;
  const width  = Math.ceil(bounds.maxY) + 1;

  const grid = Array.from({ length: width }, () => Array(length).fill('oob'));

  for (let y = 0; y < width; y++) {
    for (let x = 0; x < length; x++) {
      const { col, row } = metreToHex(x + 0.5, y + 0.5, hexSize);
      const terrain = hexMap.get(`${col},${row}`);
      if (terrain) grid[y][x] = terrain;
    }
  }

  return { grid, length, width };
}

/**
 * Create a hole layout object from a hex-format JSON file.
 *
 * Returns the same shape as holeSimulation.createHoleLayout so the simulation
 * engine (holeSimulation.js) needs no changes. Additionally exposes hexData,
 * teeHex, and pinHex for the HoleHexGrid renderer.
 *
 * @param {Object} holeData - parsed hex-format JSON
 * @returns {Object} hole layout compatible with simulateHole / simulateMultipleHoles
 */
export function createHoleLayoutFromHex(holeData) {
  const {
    hexes,
    hexSize,
    teeHex,
    pinHex,
    par,
    waypoints = [],
    dropZones = [],
  } = holeData;

  // Validate waypoints: skip any that reference a hex not in the layout (treated as OOB)
  const hexMapForValidation = buildHexMap(hexes);
  const validWaypoints = waypoints.filter(([col, row]) => {
    const terrain = hexMapForValidation.get(`${col},${row}`);
    if (!terrain) {
      console.warn(
        `[holeLoader] Waypoint [${col},${row}] is not in the hex list (OOB) — skipping.`
      );
      return false;
    }
    return true;
  });

  const hexData = { hexes, hexSize, waypoints: validWaypoints, dropZones };
  const { grid, length, width } = buildMetreGrid(hexData);

  const tee = hexCentreMetres(teeHex[0], teeHex[1], hexSize);
  const pin = hexCentreMetres(pinHex[0], pinHex[1], hexSize);

  const waypointMetres = validWaypoints.map(([col, row]) =>
    hexCentreMetres(col, row, hexSize)
  );

  const dropZoneMetres = dropZones.map(([col, row]) =>
    hexCentreMetres(col, row, hexSize)
  );

  return {
    grid,
    length,
    width,
    par,
    tee,
    pin,
    waypoints: waypointMetres,
    dropZones: dropZoneMetres,
    // Renderer extras
    hexData,
    teeHex,
    pinHex,
  };
}
